import React, { useMemo, useState } from 'react';
import {
  Dimmer,
  Icon,
  Label,
  List,
  Loader,
  Message,
  Modal,
} from 'semantic-ui-react';
import styled from 'styled-components';
import Button from '../../../../components/Button';
import {
  dangerNotification,
  successNotification,
} from '../../../../services/notification';
import { syncBatchNumbering } from '../../../../services/fire-insurance-batches';
import {
  getBatchStatusColor,
  translateBatchStatus,
} from '../types';
import { canSyncBatchNumbering } from '../utils/batch-sync-numbering.util';

export interface SyncBatchNumberingModalBatch {
  id: string;
  batchNumber: number;
  status: string;
  itemCount: number;
  quotationCode?: string | null;
  insurer?: {
    code?: string;
  };
  estate: {
    name: string;
  };
}

interface SyncBatchNumberingModalProps {
  open: boolean;
  batch: SyncBatchNumberingModalBatch | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ContextBanner = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 10px 12px;
  margin-bottom: 16px;
`;

const ContextItem = styled.span`
  color: #444;
  font-size: 13px;

  strong {
    color: #333;
  }
`;

const IntroText = styled.p`
  color: #444;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 12px;
`;

const ImplicationsList = styled(List)`
  &.ui.list {
    margin: 0 0 16px;
    color: #444;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const SyncBatchNumberingModal: React.FC<SyncBatchNumberingModalProps> = ({
  open,
  batch,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  const eligibility = useMemo(
    () =>
      batch
        ? canSyncBatchNumbering({
            status: batch.status,
            itemCount: batch.itemCount,
            quotationCode: batch.quotationCode,
            insurerCode: batch.insurer?.code,
          })
        : { allowed: false },
    [batch],
  );

  const handleClose = () => {
    if (loading) {
      return;
    }
    onClose();
  };

  const handleConfirm = async () => {
    if (!batch || !eligibility.allowed) {
      return;
    }

    setLoading(true);
    try {
      await syncBatchNumbering(batch.id);
      successNotification(
        'Sucesso',
        'Sincronização enfileirada. Você receberá um email com o relatório ao final do processamento.',
      );
      onSuccess();
      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : (error as { message?: string })?.message ||
            'Erro ao enfileirar sincronização de numeração';
      dangerNotification('Erro', message);
    } finally {
      setLoading(false);
    }
  };

  if (!batch) {
    return null;
  }

  return (
    <Modal open={open} onClose={handleClose} size="small" closeOnDimmerClick={!loading}>
      <Modal.Header>
        <Icon name="sync" />
        Sincronizar numeração
      </Modal.Header>

      <Modal.Content>
        <Dimmer active={loading} inverted>
          <Loader indeterminate>Enfileirando sincronização...</Loader>
        </Dimmer>

        <ContextBanner>
          <ContextItem>
            <strong>Lote #{batch.batchNumber}</strong>
          </ContextItem>
          <ContextItem>·</ContextItem>
          <ContextItem>{batch.estate.name}</ContextItem>
          <ContextItem>·</ContextItem>
          <Label color={getBatchStatusColor(batch.status) as any} size="tiny">
            {translateBatchStatus(batch.status)}
          </Label>
        </ContextBanner>

        <IntroText>
          O sistema consultará os itens na Yelum, comparará com as apólices deste lote e
          corrigirá automaticamente a numeração (<code>itemNo</code> /{' '}
          <code>externalPolicyCode</code>) quando houver divergência. Os certificados PDF
          das apólices corrigidas serão regenerados.
        </IntroText>

        <ImplicationsList bulleted>
          <List.Item>
            A operação é assíncrona: o job roda em fila com até 3 tentativas.
          </List.Item>
          <List.Item>
            Ao final, você receberá um email com resumo e link para relatório XLSX detalhado.
          </List.Item>
          <List.Item>
            Divergências não resolvíveis automaticamente (match ambíguo, item não encontrado,
            colisão de itemNo, itens Yelum órfãos) serão reportadas no email e no XLSX.
          </List.Item>
          <List.Item>
            Apólices canceladas/inativas são ignoradas. Itens já alinhados não terão certificado
            regenerado.
          </List.Item>
        </ImplicationsList>

        {!eligibility.allowed && eligibility.reason ? (
          <Message negative icon size="small">
            <Icon name="warning circle" />
            <Message.Content>{eligibility.reason}</Message.Content>
          </Message>
        ) : null}

        {eligibility.allowed ? (
          <Message info size="small" style={{ marginBottom: 0 }}>
            <Icon name="info circle" />
            Tem certeza de que deseja continuar?
          </Message>
        ) : null}
      </Modal.Content>

      <Modal.Actions>
        <Button basic onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          color="teal"
          onClick={handleConfirm}
          loading={loading}
          disabled={loading || !eligibility.allowed}
        >
          Confirmar sincronização
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default SyncBatchNumberingModal;
